
import { useAuth } from "@/hooks/useAuth";
import { useUserDashboardData } from "@/hooks/useUserDashboardData";
import type { UserRole, UserPermission } from "@/types/roles";
import { UserRoleType } from "@/types/user";

export type { UserRole, UserPermission };

export const useRolePermissions = () => {
  const { user } = useAuth();
  const { userRole } = useUserDashboardData();
  
  // Default role when data is not yet loaded
  const defaultRole: UserRole = {
    role: 'super',
    is_active: true,
    subscription_status: 'active',
    plan_tier: 'professional',
    created_by: null,
    team_id: null
  };

  const actualRole = userRole || defaultRole;
  
  // Role checks
  const isSuper = actualRole.role === 'super';
  const isAdmin = actualRole.role === 'admin';
  const isAdminOrHigher = isSuper || isAdmin;
  const isMember = actualRole.role === 'member';
  const isMemberOrHigher = isAdminOrHigher || isMember;
  const isViewer = actualRole.role === 'viewer';
  const isActive = actualRole.is_active !== false;
  
  // Subscription checks
  const isPremium = actualRole.plan_tier === 'premium' || actualRole.plan_tier === 'professional';
  const isProfessional = actualRole.plan_tier === 'professional';
  const isBasic = actualRole.plan_tier === 'basic';
  const isFree = actualRole.plan_tier === 'free' || !actualRole.plan_tier;
  const hasActiveSubscription = actualRole.subscription_status === 'active';
  
  // Relationship data
  const createdBy = actualRole.created_by;
  const teamId = actualRole.team_id;
  
  console.log('Role permissions calculated:', {
    userRole: actualRole,
    isSuper,
    isAdmin,
    isMember,
    isViewer,
    isActive
  });
  
  // User management permissions
  const canManageAllUsers = () => isAdminOrHigher;
  const canManageUser = () => isAdminOrHigher;
  const canManageTeam = isAdminOrHigher;
  const canAssignRole = () => isAdminOrHigher;
  
  // Page access permissions
  const canAccessAdminPanel = isAdminOrHigher;
  const canAccessDashboard = isActive;
  const canEditContent = isMemberOrHigher;
  const canViewContent = true;
  const canAccessUserSettings = true;
  const canCreateDisplays = isMemberOrHigher;
  
  // Feature permissions
  const canAccessPremiumFeatures = isPremium;
  
  // Entity-specific permissions helper
  const getEntityPermissions = (): UserPermission => ({
    canView: true,
    canEdit: isMemberOrHigher,
    canDelete: isAdminOrHigher,
    canInvite: isAdminOrHigher,
    canManage: isAdminOrHigher
  });
  
  return {
    userRole: actualRole,
    loading: false,
    
    // Role checks
    isSuper,
    isAdmin,
    isAdminOrHigher,
    isMember,
    isMemberOrHigher,
    isViewer,
    isActive,
    
    // Subscription checks
    isPremium,
    isProfessional,
    isBasic,
    isFree,
    hasActiveSubscription,
    
    // Relationship data
    createdBy,
    teamId,
    
    // User management permissions
    canManageAllUsers,
    canManageUser,
    canManageTeam,
    canAssignRole,
    
    // Page access permissions
    canAccessAdminPanel,
    canAccessDashboard,
    canEditContent,
    canViewContent,
    canAccessUserSettings,
    canCreateDisplays,
    
    // Feature permissions
    canAccessPremiumFeatures,
    
    // Entity-specific permissions helper
    getEntityPermissions,
  };
};
